import React, { useState, useEffect } from "react";
import {
  Offcanvas,
  Form,
  Button,
  Spinner,
  Table,
  Badge,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";
import { Box, Divider, Typography } from "@mui/material";
import {
  PersonPlusFill,
  ShieldLockFill,
  GeoAltFill,
  Table as TableIcon,
  XCircle,
} from "react-bootstrap-icons";

// Reusable SectionHeader component (same as in AddUserOffcanvas)
const SectionHeader = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
    <Box sx={{ color: "#0d47a1", display: "flex" }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#7b8ab8",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#dde4f5" }} />
  </Box>
);

// Reusable field style
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
  padding: "10px",
};

const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const AddPrisonerOffcanvas = ({
  show,
  handleClose,
  instructorId,
  locations,
  preSelectedLocation,
}) => {
  const [prisonerName, setPrisonerName] = useState("");
  const [location, setLocation] = useState(preSelectedLocation || "");
  const [locationList, setLocationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prisoners, setPrisoners] = useState([]);
  const [prisonerId, setPrisonerId] = useState("");

  const handleSubmit = async () => {
    try {
      const payload = {
        instructorId,
        prisonerId,
        prisonerName,
        location,
      };

      await axios.post(`${API_BASE_URL}/api/prisoner/addPrisoner`, payload);
      toast.success("Prisoner added successfully");

      setPrisonerName("");
      setPrisonerId("");
      setLocation(preSelectedLocation || "");

      handleClose();
    } catch (err) {
      toast.error("Failed to add prisoner");
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/location/admin/getAllLocations`,
      );
      const locations = res.data?.data || [];
      const activeLocations = locations.filter(
        (loc) => loc.status === "Active",
      );
      setLocationList(activeLocations);

      if (
        preSelectedLocation &&
        !activeLocations.some((loc) => loc._id === preSelectedLocation)
      ) {
        const preSelectedLoc = locations.find(
          (loc) => loc._id === preSelectedLocation,
        );
        if (preSelectedLoc) {
          setLocationList([preSelectedLoc, ...activeLocations]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch locations:", err);
      toast.error("Failed to load locations");
    }
  };

  const fetchPrisoners = async () => {
    if (!instructorId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/prisoner/prisonersByInstructor/${instructorId}`,
      );
      setPrisoners(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch prisoners:", err);
      toast.error("No prisoners load ");
      setPrisoners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchLocations();
      fetchPrisoners();
      setPrisonerName("");
      setPrisonerId("");
      setLocation(preSelectedLocation || "");
    }
  }, [show, instructorId, preSelectedLocation]);

  const getLocationName = (locationObj) => {
    if (!locationObj) return "Not assigned";
    return typeof locationObj === "string" ? locationObj : locationObj.location;
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      style={{ width: 500 }}
    >
      <Offcanvas.Header
        closeButton
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #1a237e, #0d47a1)",
          borderBottom: "none",
        }}
      >
        <Offcanvas.Title
          className="d-flex align-items-center gap-2 fw-bold"
          style={{ fontSize: "1rem" }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: "rgba(255,235,59,0.2)",
              border: "1.5px solid rgba(255,235,59,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonPlusFill size={13} color="#ffeb3b" />
          </Box>
          Add Student to Instructor
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ background: "#f0f4ff" }}>
        <Form className="p-3 rounded-3 shadow-sm bg-white">
          <SectionHeader
            icon={<PersonPlusFill size={13} />}
            label="Student Details"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Student ID <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={prisonerId}
              onChange={(e) => setPrisonerId(e.target.value)}
              placeholder="Enter Student ID"
              style={fieldStyle}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Student Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={prisonerName}
              onChange={(e) => setPrisonerName(e.target.value)}
              placeholder="Enter full name"
              style={fieldStyle}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={labelStyle}>
              Location <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              style={fieldStyle}
            >
              <option value="">Select location</option>
              {locationList.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.location}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Box display="flex" justifyContent="flex-end" gap={2} mb={4}>
            <Button
              onClick={handleClose}
              style={{
                background: "transparent",
                border: "1.5px solid #dde4f5",
                color: "#1a237e",
                borderRadius: "8px",
                fontWeight: 600,
                padding: "8px 20px",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              style={{
                background: "linear-gradient(135deg, #1a237e, #0d47a1)",
                border: "none",
                borderRadius: "8px",
                fontWeight: 600,
                padding: "8px 20px",
                boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
              }}
            >
              Add Student
            </Button>
          </Box>

          <SectionHeader
            icon={<TableIcon size={13} />}
            label={`Existing Students (${prisoners.length})`}
          />

          <div
            className="mt-3"
            style={{ borderRadius: "8px", overflow: "hidden" }}
          >
            {loading ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={4}
              >
                <Spinner animation="border" style={{ color: "#0d47a1" }} />
                <Typography variant="body2" sx={{ mt: 2, color: "#7b8ab8" }}>
                  Loading Student...
                </Typography>
              </Box>
            ) : prisoners.length > 0 ? (
              <Table
                striped
                bordered
                hover
                responsive
                size="sm"
                style={{ marginBottom: 0 }}
              >
                <thead style={{ background: "#f0f4ff" }}>
                  <tr>
                    <th style={{ color: "#1a237e", fontWeight: 600 }}>#</th>
                    <th style={{ color: "#1a237e", fontWeight: 600 }}>Name</th>
                    <th style={{ color: "#1a237e", fontWeight: 600 }}>
                      Location
                    </th>
                    <th style={{ color: "#1a237e", fontWeight: 600 }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {prisoners.map((prisoner, index) => (
                    <tr key={prisoner._id}>
                      <td>{index + 1}</td>
                      <td>{prisoner.prisonerName}</td>
                      <td>{getLocationName(prisoner.location)}</td>
                      <td>
                        <Badge
                          bg={
                            prisoner.status === "Active" ? "success" : "danger"
                          }
                          style={{
                            background:
                              prisoner.status === "Active"
                                ? "#4caf50"
                                : "#f44336",
                            borderRadius: "12px",
                            padding: "4px 8px",
                            fontWeight: 500,
                          }}
                        >
                          {prisoner.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={4}
                sx={{ background: "#fff", borderRadius: "8px" }}
              >
                <XCircle size={30} color="#dde4f5" />
                <Typography variant="body2" sx={{ mt: 2, color: "#7b8ab8" }}>
                  No students found for this instructor
                </Typography>
              </Box>
            )}
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddPrisonerOffcanvas;
